/**
 * Pricing Plan API hooks
 *
 * 이 파일은 generated hooks를 래핑하여 일관된 인터페이스를 제공합니다.
 * API 응답 타입 변환 및 에러 처리를 담당합니다.
 */

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    useGetVoucherList,
    useCreateVoucher,
    useUpdateVoucher,
    useToggleVoucher,
    useDeleteVoucher,
} from '@hooks/generated/charge';
import type { PricingPlan, PricingPlanType } from '../types';

// Query keys for cache invalidation
export const pricingPlanKeys = {
    all: ['getVoucherList'] as const,
    list: (groupId?: number, type?: PricingPlanType, withInactive?: boolean) =>
        [...pricingPlanKeys.all, groupId, withInactive, type] as const,
};

// VoucherResponse를 PricingPlan 타입으로 변환
function mapVoucherToPricingPlan(voucher: {
    id?: number;
    name?: string;
    type?: string;
    description?: string;
    totalAmount?: number;
    totalCount?: number;
    chargeAmount?: number;
    hourlyRate?: number;
    minimumMinutes?: number;
    active?: boolean;
    createdAt?: string;
    updatedAt?: string;
}, groupId: number): PricingPlan {
    return {
        id: voucher.id || 0,
        groupId,
        name: voucher.name || '',
        type: (voucher.type || 'PACKAGE') as PricingPlanType,
        description: voucher.description,
        totalAmount: voucher.totalAmount,
        totalCount: voucher.totalCount,
        chargeAmount: voucher.chargeAmount,
        hourlyRate: voucher.hourlyRate,
        minimumMinutes: voucher.minimumMinutes,
        active: voucher.active !== false,
        createdAt: voucher.createdAt || new Date().toISOString(),
        updatedAt: voucher.updatedAt || new Date().toISOString(),
    };
}

// GET - 요금제 목록 조회
interface GetPricingPlansOptions {
    request?: {
        groupId?: number;
        type?: PricingPlanType;
        withInactive?: boolean;
    };
    options?: {
        enabled?: boolean;
    };
}

export function useGetPricingPlans({ request, options }: GetPricingPlansOptions = {}) {
    const { data: vouchersData, ...query } = useGetVoucherList({
        request: {
            groupId: request?.groupId!,
            withInactive: request?.withInactive,
            type: request?.type,
        },
        options: {
            enabled: options?.enabled !== false && !!request?.groupId,
        },
    });

    // VoucherResponse[]를 PricingPlan[]로 변환
    const pricingPlans = useMemo(() => {
        if (!vouchersData || !Array.isArray(vouchersData)) return [];
        return vouchersData.map(v => mapVoucherToPricingPlan(v, request?.groupId || 0));
    }, [vouchersData, request?.groupId]);

    return {
        ...query,
        data: pricingPlans,
    };
}

// POST - 요금제 생성
interface CreatePricingPlanOptions {
    options?: {
        onSuccess?: () => void;
        onError?: (error: Error) => void;
    };
}

export function useCreatePricingPlan({ options }: CreatePricingPlanOptions = {}) {
    const queryClient = useQueryClient();

    const mutation = useCreateVoucher({
        options: {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: pricingPlanKeys.all });
                options?.onSuccess?.();
            },
            onError: options?.onError,
        },
    });

    return {
        ...mutation,
        mutate: (params: {
            groupId: number;
            name: string;
            type: PricingPlanType;
            description?: string;
            totalAmount?: number;
            totalCount?: number;
            chargeAmount?: number;
            hourlyRate?: number;
            minimumMinutes?: number;
        }) => {
            mutation.mutate({
                groupId: params.groupId,
                voucherCreateRequest: {
                    name: params.name,
                    type: params.type,
                    description: params.description,
                    totalAmount: params.totalAmount,
                    totalCount: params.totalCount,
                    chargeAmount: params.chargeAmount,
                    hourlyRate: params.hourlyRate,
                    minimumMinutes: params.minimumMinutes,
                },
            });
        },
        isPending: mutation.isPending,
    };
}

// PUT - 요금제 수정
interface UpdatePricingPlanOptions {
    options?: {
        onSuccess?: () => void;
        onError?: (error: Error) => void;
    };
}

export function useUpdatePricingPlan({ options }: UpdatePricingPlanOptions = {}) {
    const queryClient = useQueryClient();

    const mutation = useUpdateVoucher({
        options: {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: pricingPlanKeys.all });
                options?.onSuccess?.();
            },
            onError: options?.onError,
        },
    });

    return {
        ...mutation,
        mutate: (params: {
            groupId: number;
            planId: number;
            data: {
                name?: string;
                description?: string;
                totalAmount?: number;
                totalCount?: number;
                chargeAmount?: number;
                hourlyRate?: number;
                minimumMinutes?: number;
            };
        }) => {
            mutation.mutate({
                groupId: params.groupId,
                voucherId: params.planId,
                voucherUpdateRequest: {
                    name: params.data.name,
                    description: params.data.description,
                    totalAmount: params.data.totalAmount,
                    totalCount: params.data.totalCount,
                    chargeAmount: params.data.chargeAmount,
                    hourlyRate: params.data.hourlyRate,
                    minimumMinutes: params.data.minimumMinutes,
                },
            });
        },
        isPending: mutation.isPending,
    };
}

// PATCH - 활성/비활성 토글
interface TogglePricingPlanOptions {
    options?: {
        onSuccess?: (data: { id: number; active: boolean }) => void;
        onError?: (error: Error) => void;
    };
}

export function useTogglePricingPlan({ options }: TogglePricingPlanOptions = {}) {
    const queryClient = useQueryClient();

    const mutation = useToggleVoucher({
        options: {
            onSuccess: (response) => {
                queryClient.invalidateQueries({ queryKey: pricingPlanKeys.all });
                const data = response.data?.data;
                if (data) {
                    options?.onSuccess?.({ id: data.id || 0, active: data.active || false });
                }
            },
            onError: options?.onError,
        },
    });

    return {
        ...mutation,
        mutate: (params: { groupId: number; planId: number }) => {
            mutation.mutate({
                groupId: params.groupId,
                voucherId: params.planId,
            });
        },
        isPending: mutation.isPending,
    };
}

// DELETE - 요금제 삭제
interface DeletePricingPlanOptions {
    options?: {
        onSuccess?: () => void;
        onError?: (error: Error) => void;
    };
}

export function useDeletePricingPlan({ options }: DeletePricingPlanOptions = {}) {
    const queryClient = useQueryClient();

    const mutation = useDeleteVoucher({
        options: {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: pricingPlanKeys.all });
                options?.onSuccess?.();
            },
            onError: options?.onError,
        },
    });

    return {
        ...mutation,
        mutate: (params: { groupId: number; planId: number }) => {
            mutation.mutate({
                groupId: params.groupId,
                voucherId: params.planId,
            });
        },
        isPending: mutation.isPending,
    };
}
